import { useMemo } from "react";
import { Link } from "react-router-dom";
import useTableState from "@/hooks/useTableState";
import { matchSearch, sortRows, TableToolbar, Th } from "@/components/DataTable";

const mono = "font-mono2 tracking-[.04em]";
const DEFAULTS = { sort: "added_at", dir: "desc" };
const SEARCH_KEYS = ["player_name", "set_name", "card_number", "team", "subset"];

/* Dense view for big collections: every column, sort per column, instant search. */
export function CollectionTable({ rows, t, lang }) {
  const ts = useTableState("ct", DEFAULTS);
  const q = ts.get("q");
  const visible = useMemo(() => sortRows(rows.filter((r) => matchSearch(r, q, SEARCH_KEYS)), ts.get("sort"), ts.get("dir"),
    (r) => (ts.get("sort") === "quantity" ? r.quantity : r[ts.get("sort")])), [rows, q, ts]);
  const fmt = (v) => (v ? new Date(v).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB") : "—");
  return (
    <section className="border border-[var(--d-line)] bg-[var(--d-panel)] px-4 py-3" data-testid="collection-table">
      <h2 className="mb-3 font-sg text-sm font-bold uppercase tracking-[-0.02em] text-[var(--d-text)]">{t("coll.table_title")} <span className={`${mono} text-[10px] text-[var(--d-muted)]`} data-testid="collection-table-count">{visible.length}</span></h2>
      <TableToolbar ts={ts} t={t} placeholder={t("coll.search_ph")} />
      <div className="overflow-x-auto">
        <table className={`${mono} w-full text-[11px]`}>
          <thead className="text-[9px] uppercase text-[var(--d-muted)]">
            <tr className="border-b border-[var(--d-line)] text-left">
              <Th k="set_name" label={t("coll.col_set")} ts={ts} />
              <Th k="subset" label={t("coll.col_subset")} ts={ts} />
              <Th k="card_number" label={t("coll.col_number")} ts={ts} />
              <Th k="player_name" label={t("coll.col_player")} ts={ts} />
              <Th k="team" label={t("coll.col_team")} ts={ts} />
              <Th k="is_rookie" label={t("coll.col_rc")} ts={ts} />
              <Th k="quantity" label={t("coll.col_qty")} ts={ts} className="text-right" />
              <Th k="added_at" label={t("coll.col_added")} ts={ts} />
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r.card_id} className="border-b border-[var(--d-line)]/60 hover:bg-[#0B0E14]" data-testid={`collection-table-row-${r.card_id}`}>
                <td className="py-1.5 pr-3"><Link to={`/cards/${r.set_slug}`} className="text-[var(--d-text)] hover:text-[var(--d-accent)]">{r.set_name}</Link></td>
                <td className="pr-3 text-[var(--d-muted)]">{r.subset}</td>
                <td className="pr-3 text-[var(--d-text)]"><Link to={`/cards/${r.card_id}`} className="hover:text-[var(--d-accent)]">#{r.card_number || "—"}</Link></td>
                <td className="pr-3">{r.player_id ? <Link to={`/players/${r.player_id}`} className="font-bold text-[var(--d-text)] hover:text-[var(--d-accent)]">{r.player_name}</Link> : <span className="text-[var(--d-text)]">{r.player_name}</span>}</td>
                <td className="pr-3 text-[var(--d-muted)]">{r.team || "—"}</td>
                <td className="pr-3 text-[var(--d-accent)]">{r.is_rookie ? "RC" : ""}</td>
                <td className="pr-3 text-right tabular-nums text-[var(--d-text)]">{r.quantity}</td>
                <td className="pr-3 text-[var(--d-muted)]">{fmt(r.added_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
